import ConfirmDialog from '@components/Common/ConfirmDialog';
import { SectionCard } from '@components/Common/EntityWorkspace';
import { resolveResumeUrl } from '@components/Common/resumeUrl';
import { uploadToS3Direct } from '@components/Common/directUpload';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { useCurrentUserRole } from '@components/Common/Utils/roles';
import type { PortfolioItem } from '@components/JobSeekers/types';
import {
  addJobSeekerPortfolioItemsMutation,
  removeJobSeekerPortfolioItemMutation,
} from '@constants/graphQLQueries';
import { formatDayMonth } from '@helpers/date';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import { Alert, Box, Button, Chip, IconButton, LinearProgress } from '@mui/material';
import React, { useRef, useState } from 'react';

import {
  MAX_SAMPLES,
  SAMPLE_ACCEPT,
  SAMPLE_BUCKET,
  canEditWorkSamples,
  countLabel,
  failureMessage,
  failureReason,
  humanSize,
  normalizePortfolio,
  planUploads,
  sampleName,
  sampleObjectUrl,
  type RejectedSample,
} from './workSamples';
import './index.scss';

interface WorkSamplesProps {
  jobSeekerId: string;
  items: PortfolioItem[];
  /** The candidate's first name, for the remove dialog. */
  candidateName?: string;
  /** A line under the grid that only this page has, e.g. who sees these after submission. */
  footnote?: string;
  /** Staff id to name, for "Added by Mintly · Ritu". An id it cannot name is left off. */
  resolveName?: (_id?: string) => string;
  /**
   * The list after an add or a remove, as the mutation returned it; null when the response did not
   * carry it and the page should reload the record instead.
   */
  onChanged: (_next: PortfolioItem[] | null) => void;
}

interface UploadRow {
  name: string;
  size: number;
  status: 'waiting' | 'uploading' | 'done' | 'failed';
  progress: number;
}

/**
 * A candidate's work samples: designs, renders, photographs of finished pieces, or a PDF portfolio.
 *
 * One component for both places an HR meets a candidate's work, the candidate page and the
 * application (submission) page, so the two cannot drift. Super Admin and the recruiting team add
 * and remove; every other role sees the same grid with no controls (the BFF and the backend refuse
 * the two mutations to Marketing whatever this renders).
 *
 * Adding uploads the picked files one at a time straight to S3, then records all that landed in one
 * `addJobSeekerPortfolioItems`. A file that fails is named and the rest are still saved: losing
 * three good renders because the fourth was a 40 MB TIFF would be the wrong trade. Removing drops
 * the item from the profile (the file stays in S3; deleting objects is Super Admin only).
 */
const WorkSamples: React.FC<WorkSamplesProps> = ({
  jobSeekerId,
  items,
  candidateName,
  footnote,
  resolveName,
  onChanged,
}) => {
  const role = useCurrentUserRole();
  const editable = canEditWorkSamples(role);
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [rows, setRows] = useState<UploadRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PortfolioItem | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const uploading = rows.length > 0 || saving;
  const full = items.length >= MAX_SAMPLES;
  const current = rows.findIndex((r) => r.status === 'waiting' || r.status === 'uploading');

  const patchRow = (i: number, patch: Partial<UploadRow>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const pick = () => inputRef.current?.click();

  const onPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Cleared at once, so picking the same file again after a failure still fires a change.
    e.target.value = '';
    if (files.length === 0) return;

    const { accepted, rejected } = planUploads(files, items.length);
    const failed: RejectedSample[] = [...rejected];
    setFailure(null);
    setRows(accepted.map(({ file }) => ({ name: file.name, size: file.size, status: 'waiting', progress: 0 })));

    const uploaded: Array<{ imageUrl: string; caption: string; fileType: string }> = [];
    // One at a time, on purpose: the order is the order picked, and a per-file bar that moves one
    // row at a time is what makes a slow connection read as progress rather than as a hang.
    for (const [i, { file, fileType }] of accepted.entries()) {
      patchRow(i, { status: 'uploading' });
      try {
        const key = await uploadToS3Direct(SAMPLE_BUCKET, file, {
          fileName: file.name,
          onProgress: (fraction) => patchRow(i, { progress: fraction }),
        });
        uploaded.push({ imageUrl: sampleObjectUrl(key), caption: file.name, fileType });
        patchRow(i, { status: 'done', progress: 1 });
      } catch (err) {
        failed.push({ name: file.name, reason: failureReason(err) });
        patchRow(i, { status: 'failed' });
      }
    }

    if (uploaded.length > 0) {
      setSaving(true);
      try {
        const { data } = await fetchGraphQLApiData(addJobSeekerPortfolioItemsMutation(jobSeekerId, uploaded));
        const next = data?.addJobSeekerPortfolioItems?.portfolio;
        onChanged(Array.isArray(next) ? normalizePortfolio(next) : null);
        showToast(uploaded.length === 1 ? 'Sample added' : `${uploaded.length} samples added`, 'success');
      } catch (err) {
        const reason = failureReason(err, 'it could not be saved to the profile');
        uploaded.forEach((u) => failed.push({ name: u.caption, reason }));
      } finally {
        setSaving(false);
      }
    }

    setRows([]);
    setFailure(failureMessage(failed));
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      const { data } = await fetchGraphQLApiData(removeJobSeekerPortfolioItemMutation(jobSeekerId, removing.imageUrl));
      const next = data?.removeJobSeekerPortfolioItem?.portfolio;
      onChanged(Array.isArray(next) ? normalizePortfolio(next) : null);
      showToast('Sample removed', 'success');
      setRemoving(null);
    } catch (err) {
      showToast(`Could not remove it: ${failureReason(err, 'try again')}.`, 'error');
    } finally {
      setRemoveBusy(false);
    }
  };

  /** A PDF opens through the BFF's signed download, the same route a résumé uses. */
  const openPdf = async (e: React.MouseEvent<HTMLAnchorElement>, item: PortfolioItem) => {
    e.preventDefault();
    const url = await resolveResumeUrl(item.imageUrl);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
    else showToast('Could not open the PDF', 'error');
  };

  const addedLine = (item: PortfolioItem): string => {
    const when = item.uploadedAt ? formatDayMonth(item.uploadedAt, '') : '';
    if (item.addedBy !== 'mintly') return ['Candidate', when].filter(Boolean).join(' · ');
    const who = item.addedById && resolveName ? resolveName(item.addedById) : '';
    // resolveName answers the raw id when it cannot name someone; an ObjectId helps nobody here.
    return [who && who !== item.addedById ? who : '', when].filter(Boolean).join(' · ');
  };

  const first = candidateName?.trim() || '';

  const addButton = editable && items.length > 0 && (
    <Button
      variant="outlined"
      size="small"
      startIcon={<AddIcon />}
      onClick={pick}
      disabled={uploading || full}
    >
      Add samples
    </Button>
  );

  const meta = uploading && rows.length > 0
    ? `Uploading ${current === -1 ? rows.length : current + 1} of ${rows.length}`
    : countLabel(items.length, editable);

  return (
    <SectionCard title="Work samples" icon={<CollectionsOutlinedIcon />} meta={meta} actions={addButton || undefined}>
      <Box className="cc-work-samples">
        {editable && (
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={SAMPLE_ACCEPT}
            className="cc-work-samples__input"
            aria-label="Choose work samples"
            onChange={onPicked}
          />
        )}

        {rows.length > 0 && (
          <Box className="cc-work-samples__progress" aria-live="polite">
            {rows.map((r, i) => (
              <Box className="cc-work-samples__progress-row" key={`${i}-${r.name}`}>
                <Box className="cc-work-samples__file">
                  <span className="cc-work-samples__file-name">{r.name}</span>
                  <span className="cc-work-samples__file-state">
                    {r.status === 'uploading' ? humanSize(r.size) : r.status}
                  </span>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.round(r.progress * 100)}
                  className={`cc-work-samples__bar${r.status === 'failed' ? ' cc-work-samples__bar--failed' : ''}`}
                  aria-label={`${r.name} upload`}
                />
              </Box>
            ))}
            <span className="cc-work-samples__help">
              Files upload one at a time. If one fails, the others are kept and the failed one is named.
            </span>
          </Box>
        )}

        {failure && (
          <Alert severity="error" onClose={() => setFailure(null)} className="cc-work-samples__failure">
            {failure}
          </Alert>
        )}

        {items.length === 0 && rows.length === 0 && (
          <Box className="cc-work-samples__empty">
            <span className="cc-work-samples__empty-title">No work samples yet</span>
            <span className="cc-work-samples__help">
              {editable
                ? 'Add designs, renders, photos of finished pieces or a PDF portfolio. The candidate can see and remove anything you add.'
                : 'Neither the candidate nor Mintly has added any.'}
            </span>
            {editable && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={pick} disabled={uploading}>
                Add samples
              </Button>
            )}
          </Box>
        )}

        {items.length > 0 && (
          <Box className="cc-work-samples__grid">
            {items.map((item) => {
              const name = sampleName(item);
              const line = addedLine(item);
              return (
                <figure className="cc-work-samples__tile" key={item.imageUrl} data-file-type={item.fileType}>
                  <a
                    href={item.imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cc-work-samples__open"
                    aria-label={`Open ${name}`}
                    onClick={item.fileType === 'pdf' ? (e) => openPdf(e, item) : undefined}
                  >
                    {item.fileType === 'pdf' ? (
                      <span className="cc-work-samples__pdf" aria-hidden="true">
                        <span className="cc-work-samples__doc">
                          <span className="cc-work-samples__doc-line" />
                          <span className="cc-work-samples__doc-line" />
                          <span className="cc-work-samples__doc-line" />
                          <b className="cc-work-samples__doc-label">PDF</b>
                        </span>
                      </span>
                    ) : (
                      <img src={item.imageUrl} alt={name} className="cc-work-samples__img" loading="lazy" />
                    )}
                  </a>
                  <figcaption className="cc-work-samples__info">
                    <span className="cc-work-samples__caption" title={name}>{name}</span>
                    {(item.addedBy === 'mintly' || line) && (
                      <span className="cc-work-samples__who">
                        {item.addedBy === 'mintly' && (
                          <Chip size="small" label="Added by Mintly" className="cc-work-samples__chip" />
                        )}
                        {line && <span>{line}</span>}
                      </span>
                    )}
                  </figcaption>
                  {editable && (
                    <IconButton
                      size="small"
                      className="cc-work-samples__remove"
                      aria-label={`Remove ${name}`}
                      onClick={() => setRemoving(item)}
                      disabled={uploading}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </figure>
              );
            })}
          </Box>
        )}

        {items.length > 0 && (
          <span className="cc-work-samples__help">
            {editable
              ? full
                ? `This profile holds ${MAX_SAMPLES} samples, the most it can. Remove one to add another.`
                : "Images (JPG, PNG, WEBP) or PDF, up to 10 MB each. Click a tile to open it. Removing a sample hides it from the candidate's profile and from employers."
              : 'Click a tile to open it.'}
          </span>
        )}
        {footnote && <span className="cc-work-samples__help">{footnote}</span>}
      </Box>

      <ConfirmDialog
        open={removing !== null}
        title="Remove this sample?"
        body={removing ? (
          <>
            &ldquo;{sampleName(removing)}&rdquo; will no longer show on {first ? `${first}'s` : "the candidate's"} profile
            or to employers. {first || 'The candidate'} can upload it again if they want it back.
          </>
        ) : ''}
        actionLabel="Remove"
        busy={removeBusy}
        onCancel={() => setRemoving(null)}
        onConfirm={confirmRemove}
      />
    </SectionCard>
  );
};

export default WorkSamples;
